import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import './Headline.scss';
import HeadlineItem from './HeadlineItem';
import HeadlineModal from './HeadlineModal';
import HeadlineCreateModal from './HeadlineCreateModal';
import ShareModal from '../Shared/ShareModal';
import SubscriptionUpsellModal from '../Settings/Subscription/SubscriptionUpsellModal';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Alerts/Toast';
import { getAccessToken } from "../../awsAuth";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
const FALLBACK_AVATAR = 'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
const safeJSON = (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } };
const avatarKey = (uid) => `avatarUrl:${uid}`;

const pickAvatar = (v) =>
  v?.avatar || v?.avatarUrl || v?.photoURL || v?.picture || v?.profile?.avatarUrl || v?.user?.avatarUrl || '';

const CACHE_KEY = 'headline_cache_v2';
const CACHE_TTL_MS = 2 * 60 * 1000;

const nowMs = () => Date.now();
const toMs = (ts) => (ts < 1e12 ? ts * 1000 : ts);
const isExpired = (h) => toMs(h.expireAt || 0) <= nowMs();

function groupHeadlinesByUser(items) {
  const map = {};

  for (const it of items) {
    if (!it || !it.userId) continue;

    if (!map[it.userId]) {
      map[it.userId] = {
        userId: it.userId,
        authorName: it.authorName || '',
        authorAvatar: it.authorAvatar || '',
        items: [],
      };
    }

    map[it.userId].items.push(it);

    if (!map[it.userId].authorName && it.authorName) map[it.userId].authorName = it.authorName;
    if (!map[it.userId].authorAvatar && it.authorAvatar) map[it.userId].authorAvatar = it.authorAvatar;
  }

  for (const uid of Object.keys(map)) {
    map[uid].items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    map[uid].latest = map[uid].items[0] || null;
    map[uid].progressItems = map[uid].items.map((h) => ({
      id: h.id,
      createdAt: h.createdAt,
      expireAt: h.expireAt,
      isPromoted: !!h.isPromoted,
    }));
    map[uid].isPromoted = !!map[uid].items.find((x) => x.isPromoted);
  }

  return map;
}

export default function Headline() {
  const auth = useAuth();
  const { userId } = auth || {};
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [postErr, setPostErr] = useState('');
  const [postErrIsLimit, setPostErrIsLimit] = useState(false);
  const [storyLimits, setStoryLimits] = useState(null); // { limit, active, remaining } from GET /api/headlines/limits
  const [upsellOpen, setUpsellOpen] = useState(false);

  const [openCtx, setOpenCtx] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', category: '', citySlug: '' });

  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareText, setShareText] = useState('');
  const [sellerStrip, setSellerStrip] = useState([]);

  const tickRef = useRef(null);
  const reqIdRef = useRef(0);
  const abortRef = useRef(null);
  const silentKickRef = useRef(false);
  const pageVisibleRef = useRef(!document.hidden);
  const rowRef = useRef(null);

  const city = useMemo(() => safeGet('citySlug') || '', []);

  const buildAuthHeaders = React.useCallback(async () => {
    const token = await getAccessToken().catch(() => null);
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  const goAuth = useMemo(() => {
    const next = encodeURIComponent(location.pathname + location.search);
    return () => navigate(`/auth?next=${next}`);
  }, [location.pathname, location.search, navigate]);

  // Backend is the source of truth for the actual limit — this is display
  // only, so the UI can show "X/5 Stories" instead of the user finding out
  // only after a failed publish attempt.
  const fetchStoryLimits = React.useCallback(async () => {
    if (!userId) {
      setStoryLimits(null);
      return;
    }
    try {
      const headers = await buildAuthHeaders();
      const { data } = await axios.get(`${API_BASE}/api/headlines/limits`, { headers });
      setStoryLimits({
        limit: Number(data?.limit || 1),
        active: Number(data?.active || 0),
        remaining: Number(data?.remaining || 0),
      });
    } catch {
      setStoryLimits(null);
    }
  }, [userId, buildAuthHeaders]);

  useEffect(() => {
    fetchStoryLimits();
  }, [fetchStoryLimits]);

  const initialAvatar = useMemo(() => {
    if (!userId) return '';
    const fromAuth = pickAvatar(auth);
    const fromLS = safeGet(avatarKey(userId));
    const fromObj = pickAvatar(safeJSON('user') || safeJSON('me'));
    return fromAuth || fromLS || fromObj || '';
  }, [auth, userId]);

  const [myAvatar, setMyAvatar] = useState(initialAvatar);

  useEffect(() => {
    if (!userId) {
      setMyAvatar('');
      try {
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('avatarUrl:')) localStorage.removeItem(k);
        });
      } catch {}
      return;
    }

    const a = pickAvatar(auth) || safeGet(avatarKey(userId)) || pickAvatar(safeJSON('user') || safeJSON('me')) || '';
    setMyAvatar(a);
    if (a) safeSet(avatarKey(userId), a);
  }, [userId, auth]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAvatarAttempt(tryN = 1) {
      if (!userId) return;

      try {
        const headers = await buildAuthHeaders();
        const { data } = await axios.get(`${API_BASE}/api/account/${userId}`, {
          headers,
          timeout: 8000,
        });

        const found = data?.avatar || pickAvatar(data) || '';

        if (found && !cancelled) {
          setMyAvatar(found);
          safeSet(avatarKey(userId), found);
        } else if (!found && tryN < 2) {
          setTimeout(() => fetchAvatarAttempt(tryN + 1), 400);
        }
      } catch {
        if (tryN < 2) {
          setTimeout(() => fetchAvatarAttempt(tryN + 1), 600);
        }
      }
    }

    fetchAvatarAttempt(1);

    return () => {
      cancelled = true;
    };
  }, [userId, auth, buildAuthHeaders]);

  useEffect(() => {
    if (!userId) return;

    const key = avatarKey(userId);

    const onStorage = (e) => {
      if (e.key === key && e.newValue && e.newValue !== myAvatar) {
        setMyAvatar(e.newValue);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [myAvatar, userId]);

  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (loading && items.length === 0) {
      const t = setTimeout(() => setShowSkeleton(true), 250);
      return () => clearTimeout(t);
    }

    setShowSkeleton(false);
  }, [loading, items.length]);

  async function fetchHeadlines({ cursor, silent } = {}) {
    const reqId = ++reqIdRef.current;

    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
    }

    abortRef.current = new AbortController();

    if (!silent) {
      setLoading(true);
      setError('');
    } else if (!items?.length) {
      silent = false;
      setLoading(true);
      setError('');
    }

    try {
      const params = {};
      if (city) params.city = city;
      if (cursor) params.cursor = cursor;

      const headers = await buildAuthHeaders();

      const { data } = await axios.get(`${API_BASE}/api/headlines`, {
        params,
        headers,
        timeout: 18000,
        signal: abortRef.current.signal,
      });

      const list = (data?.items || []).filter((h) => !isExpired(h) && h.status !== 'archived');
      const next = data?.nextCursor || null;

      if (reqId !== reqIdRef.current) return;

      setItems(cursor ? (prev) => [...prev, ...list] : list);
      setNextCursor(next);

      safeSet(
        CACHE_KEY,
        JSON.stringify({
          ts: Date.now(),
          items: cursor ? [...items, ...list] : list,
          nextCursor: next,
        })
      );
    } catch (err) {
      if (axios.isCancel?.(err)) return;

      if (reqId === reqIdRef.current && !silent) {
        const hasData = Array.isArray(items) && items.length > 0;

        if (!hasData) {
          const st = err?.response?.status;
          const msg = st ? `Не вдалося завантажити Stories (HTTP ${st})` : 'Не вдалося завантажити Stories';
          setError(msg);
        }
      }
    } finally {
      if (reqId === reqIdRef.current) {
        setLoading(false);
      }
    }
  }

  async function fetchSellerStrip() {
    try {
      const { data } = await axios.get(`${API_BASE}/api/headlines/sellers-strip`, {
        params: {
          viewerId: userId || '',
        },
        timeout: 12000,
      });

      setSellerStrip(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setSellerStrip([]);
    }
  }

  useEffect(() => {
    const cached = safeJSON(CACHE_KEY);
    const fresh = cached && Date.now() - (cached.ts || 0) < CACHE_TTL_MS && Array.isArray(cached.items);

    if (fresh && cached.items.length > 0) {
      setItems(cached.items || []);
      setNextCursor(cached.nextCursor || null);
      setLoading(false);

      if (!silentKickRef.current) {
        silentKickRef.current = true;
        setTimeout(() => fetchHeadlines({ silent: true }), 600);
      }
    } else {
      setLoading(true);
      fetchHeadlines({ silent: false });
    }
  }, [city, userId]);

  useEffect(() => {
    fetchSellerStrip();
  }, [userId]);

  useEffect(() => {
    tickRef.current = setInterval(() => {
      setItems((prev) => [...prev]);
      fetchSellerStrip();
    }, 30000);

    return () => clearInterval(tickRef.current);
  }, [userId]);

  useEffect(() => {
    const onVis = () => {
      pageVisibleRef.current = !document.hidden;

      if (!document.hidden) {
        if (!items?.length && !loading) {
          fetchHeadlines({ silent: false });
        }

        fetchSellerStrip();
      }
    };

    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [items, loading, userId]);

  useEffect(() => {
    if (!form.citySlug && city) {
      setForm((f) => ({ ...f, citySlug: city }));
    }
  }, [city, form.citySlug]);

  async function handleCreate(e, { photoFile, design, tags } = {}) {
    e.preventDefault();

    if (!userId) {
      goAuth();
      return;
    }

    const titleTrim = (form.title || '').trim();

    if (!titleTrim || titleTrim.length < 6) {
      setPostErr('Заголовок мінімум 6 символів');
      return;
    }

    if (titleTrim.length > 100) {
      setPostErr('Максимум 100 символів');
      return;
    }

    if (!form.category) {
      setPostErr('Оберіть категорію');
      return;
    }

    setPostErr('');

    try {
      const fd = new FormData();

      fd.append('title', titleTrim);
      fd.append('description', (form.description || '').trim());
      fd.append('category', String(form.category || ''));
      fd.append('citySlug', String(form.citySlug || city || ''));

      if (Array.isArray(tags) && tags.length > 0) {
        fd.append('tags', JSON.stringify(tags));
      }

      if (design) {
        try {
          fd.append('design', JSON.stringify(design));
        } catch {}
      }

      if (photoFile) {
        fd.append('photo', photoFile);
      }

      const headers = await buildAuthHeaders();

      const { data } = await axios.post(`${API_BASE}/api/headlines`, fd, {
        headers,
        timeout: 30000,
      });

      setItems((prev) => [data, ...prev]);
      safeSet(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: [data, ...(items || [])], nextCursor }));

      setCreating(false);
      setForm({ title: '', description: '', category: '', citySlug: city || '' });

      if (data?.authorAvatar && data.authorAvatar !== myAvatar) {
        setMyAvatar(data.authorAvatar);
        safeSet(avatarKey(userId), data.authorAvatar);
      }

      setPostErrIsLimit(false);
      fetchSellerStrip();
      fetchStoryLimits();
    } catch (e2) {
      const st = e2?.response?.status;
      const d = e2?.response?.data;

      if (st === 429) {
        // Backend already rejected before writing anything (server is the
        // source of truth for the limit) — just show the real limit/active
        // numbers it returned. No fake "replace oldest" retry: the backend
        // has no such endpoint, so that flow always just failed again.
        const limit = Number(d?.limit || 1);
        const active = Number(d?.active ?? limit);
        setPostErr(d?.error || `Ліміт активних Stories (${active}/${limit}) вичерпано.`);
        setPostErrIsLimit(limit <= 1);
        setStoryLimits({ limit, active, remaining: 0 });
      } else {
        setPostErr(d?.error || 'Помилка створення');
        setPostErrIsLimit(false);
      }
    }
  }

  async function handleDeleteHeadline(h) {
    if (!h?.id || !userId) return;

    try {
      const headers = await buildAuthHeaders();

      await axios.delete(`${API_BASE}/api/headlines/${h.id}`, {
        headers,
        timeout: 12000,
      });

      const newItems = items.filter((x) => x.id !== h.id);

      setItems(newItems);
      safeSet(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: newItems, nextCursor }));
      setOpenCtx(null);
      fetchSellerStrip();
      fetchStoryLimits();
    } catch (e) {
      alert(e?.response?.data?.error || 'Не вдалося видалити Story');
    }
  }

  function goRespond(h) {
    navigate(`/messages?to=${encodeURIComponent(h.userId)}&ctx=headline:${encodeURIComponent(h.id)}`);
  }

  function openShareFor(h) {
    if (!h) return;

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}/headline/${encodeURIComponent(h.id)}`;

    setShareUrl(url);
    setShareText(h.title || 'Story');
    setShareOpen(true);
  }

  const activeItems = useMemo(() => items.filter((h) => !isExpired(h)), [items]);
  const grouped = useMemo(() => groupHeadlinesByUser(activeItems), [activeItems]);
  const myGroup = useMemo(() => (userId ? grouped[userId] || null : null), [grouped, userId]);

  const otherGroups = useMemo(() => {
    return Object.values(grouped)
      .filter((g) => g.userId !== userId)
      .sort((a, b) => (b.latest?.createdAt || 0) - (a.latest?.createdAt || 0));
  }, [grouped, userId]);

  const sellerTiles = useMemo(() => {
    const sellerMap = new Map(
      (sellerStrip || []).map((s) => [String(s.userId), s])
    );

    const used = new Set();

    if (userId) {
      used.add(String(userId));
    }

    const storyTiles = otherGroups.map((g) => {
      used.add(String(g.userId));

      const sellerMeta = sellerMap.get(String(g.userId)) || {};

      return {
        kind: 'story',
        userId: g.userId,
        title: g.latest?.title,
        username: g.authorName || sellerMeta.username || 'user',
        avatarUrl: g.authorAvatar || sellerMeta.avatar || FALLBACK_AVATAR,
        progressItems: g.progressItems,
        isPromoted: !!g.isPromoted,
        headlineId: g.latest?.id,
        hasStories: true,
        isOnline: !!sellerMeta.isOnline,
        newListingsCount: Number(sellerMeta.newListingsCount || 0),
      };
    });

    const fallbackTiles = (sellerStrip || [])
      .filter((s) => s?.userId && !used.has(String(s.userId)))
      .map((s) => ({
        kind: 'seller',
        userId: s.userId,
        title: s.username,
        username: s.username || 'user',
        avatarUrl: s.avatar || FALLBACK_AVATAR,
        progressItems: [],
        isPromoted: false,
        headlineId: null,
        hasStories: false,
        isOnline: !!s.isOnline,
        newListingsCount: Number(s.newListingsCount || 0),
      }));

    return [...storyTiles, ...fallbackTiles].slice(0, 20);
  }, [otherGroups, sellerStrip, userId]);

  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);

    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isDesktop = vw >= 992;
  const toast = useToast();

  // Stories are published from the app only; on desktop the entry points just explain.
  const requestCreateStory = () => {
    if (isDesktop) {
      toast.info('Створення Stories доступне в застосунку Cvanto\nВідкрийте Cvanto на смартфоні, щоб опублікувати Story.', 4500);
      return;
    }
    setCreating(true);
  };

  const totalTiles = 1 + sellerTiles.length;

  const layoutVariant = useMemo(() => {
    if (totalTiles <= 1) return 'center';
    const spreadLimit = isDesktop ? 8 : 4;
    return totalTiles <= spreadLimit ? 'spread' : 'scroll';
  }, [totalTiles, isDesktop]);

  useEffect(() => {
    if (!userId) return;

    if (myGroup?.authorAvatar && myGroup.authorAvatar !== myAvatar) {
      setMyAvatar(myGroup.authorAvatar);
      safeSet(avatarKey(userId), myGroup.authorAvatar);
    }
  }, [myGroup, myAvatar, userId]);

  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));

    if (!silentKickRef.current && items.length > 0) {
      idle(() => fetchHeadlines({ silent: true }));
      silentKickRef.current = true;
    }
  }, [items.length]);

  const selected = useMemo(() => {
    return openCtx ? (items.find((i) => i.id === openCtx.headlineId) || null) : null;
  }, [items, openCtx]);

  const selectedGroup = useMemo(() => {
    return selected ? (grouped[selected.userId] || null) : null;
  }, [grouped, selected]);

  const initialIndex = useMemo(() => {
    if (!selectedGroup || !selected) return 0;

    const idx = selectedGroup.items.findIndex((x) => x.id === selected.id);
    return idx >= 0 ? idx : 0;
  }, [selectedGroup, selected]);

  const carouselItemsForModal = selectedGroup && Array.isArray(selectedGroup.items)
    ? selectedGroup.items
    : null;

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch {}
      }
    };
  }, []);

  const scrollHeadlineRow = (dir) => {
    const el = rowRef.current;

    if (!el) return;

    el.scrollBy({
      left: dir * 360,
      behavior: 'smooth',
    });
  };

  return (
    <section className="headline">
      {!!error && items.length === 0 && (
        <div className="headline__error">{error}</div>
      )}

      <div className="headline__rowWrap">
        {isDesktop && sellerTiles.length > 6 && (
          <button
            type="button"
            className="headline__arrow headline__arrow--left"
            onClick={() => scrollHeadlineRow(-1)}
            aria-label="Прокрутити продавців вліво"
          >
            ‹
          </button>
        )}

        <div
          ref={rowRef}
          className={`headline__row headline__row--${layoutVariant}`}
          role="list"
        >
          {showSkeleton && (
            <>
              {Array.from({ length: 7 }).map((_, i) => (
                <div className="headline__skeleton" key={i} aria-hidden="true">
                  <div className="headline__skeleton-circle" />
                  <div className="headline__skeleton-name" />
                  <div className="headline__skeleton-desc" />
                </div>
              ))}
            </>
          )}

          {!showSkeleton && (myGroup ? (
            <HeadlineItem
              key="mine"
              mode="mine"
              title={myGroup.latest?.title}
              username="Мій Story"
              avatarUrl={myAvatar || myGroup.authorAvatar || FALLBACK_AVATAR}
              authorId={myGroup.userId}
              progressItems={myGroup.progressItems}
              isPromoted={!!myGroup.isPromoted}
              onClick={() => setOpenCtx({ headlineId: myGroup.latest?.id, authorId: myGroup.userId })}
              showAvatar={true}
              fallbackAvatar={FALLBACK_AVATAR}
            />
          ) : (
            <HeadlineItem
              key="create"
              mode="create"
              username="додати Story"
              avatarUrl={userId ? (myAvatar || FALLBACK_AVATAR) : ''}
              authorId={null}
              progressItems={[]}
              isPromoted={false}
              onClick={() => (userId ? requestCreateStory() : goAuth())}
              showAvatar={!!userId}
              plusCentered={!userId}
              fallbackAvatar={FALLBACK_AVATAR}
            />
          ))}

          {!showSkeleton && sellerTiles.map((tile) => (
            <HeadlineItem
              key={`${tile.kind}-${tile.userId}`}
              title={tile.title}
              username={tile.username}
              avatarUrl={tile.avatarUrl || FALLBACK_AVATAR}
              authorId={tile.userId}
              progressItems={tile.progressItems}
              isPromoted={!!tile.isPromoted}
              hasStories={!!tile.hasStories}
              isOnline={!!tile.isOnline}
              newListingsCount={Number(tile.newListingsCount || 0)}
              onClick={() => {
                if (tile.hasStories && tile.headlineId) {
                  setOpenCtx({ headlineId: tile.headlineId, authorId: tile.userId });
                } else {
                  navigate(`/profile/${tile.userId}`);
                }
              }}
              showAvatar={true}
              fallbackAvatar={FALLBACK_AVATAR}
            />
          ))}

          {!showSkeleton && sellerTiles.length > 0 && (
            <button
              type="button"
              className="headline__moreSellers"
              onClick={() => navigate('/search?tab=users')}
            >
              <span className="headline__moreSellersCircle">→</span>
              <span className="headline__moreSellersText">Більше продавців</span>
            </button>
          )}
        </div>

        {isDesktop && sellerTiles.length > 6 && (
          <button
            type="button"
            className="headline__arrow headline__arrow--right"
            onClick={() => scrollHeadlineRow(1)}
            aria-label="Прокрутити продавців вправо"
          >
            ›
          </button>
        )}
      </div>

      {nextCursor && !loading && (
        <div className="headline__more">
          <button
            className="headline__btn ghost"
            onClick={() => fetchHeadlines({ cursor: nextCursor, silent: false })}
          >
            Показати ще
          </button>
        </div>
      )}

      {openCtx && selected && (
        <HeadlineModal
          headline={selected}
          carouselItems={carouselItemsForModal}
          initialIndex={initialIndex}
          isAuthor={selected.userId === userId}
          onClose={() => setOpenCtx(null)}
          onRespond={() => {
            if (selected.userId === userId) requestCreateStory();
            else goRespond(selected);
          }}
          onShare={() => openShareFor(selected)}
          onSave={() => {}}
          onDelete={() => handleDeleteHeadline(selected)}
          onGoProfile={(uid) => navigate(`/profile/${uid}`)}
        />
      )}

      {creating && (
        <HeadlineCreateModal
          avatarUrl={userId ? (myAvatar || FALLBACK_AVATAR) : ''}
          form={form}
          setForm={setForm}
          posting={false}
          postErr={postErr}
          storyLimits={storyLimits}
          showUpgradeCta={postErrIsLimit}
          onUpgradeClick={() => setUpsellOpen(true)}
          onSubmit={handleCreate}
          onClose={() => setCreating(false)}
        />
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={shareUrl}
        shareText={shareText}
      />

      <SubscriptionUpsellModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        reason="story_limit"
        onTrialActivated={() => {
          setPostErr('');
          setPostErrIsLimit(false);
          fetchStoryLimits();
        }}
      />
    </section>
  );
}